import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Box, FormHelperText } from "@mui/material";
import { MDTypography } from "../../../../../Component/Common/Components/MDTypography";
import { Quill } from "../../../../../Component/Common/Components/Quill/Quill";
import { disputeResolutionsSchema } from "../DisputeResolutions/DisputeResolutionsValidation";
import { DisputeResolutionsService } from "../../../../../Component/Api/EmploymentEquity/Plan/Dispute Resolutions/DisputeResolutionsService";
import { DisputeResolutionsDto } from "../../../../../Component/Model/Dto/EmploymentEquity/Plan/EEDisputeResolutions";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { EEScorecardAtom } from "../../../../../State/EEScorecard.Atom";
import { ConsolidationAtom } from "../../../../../State/Consolidation.Atom";
import { UserActivityService } from "../../../../../Component/Api/UserActivity/UserActivityService";
import { DisputeResolutionsSection } from "../../../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../../../Component/Model/Enum/BEE123Enums";

interface IProps {
    scorecardId : string;
}

type InputForm = {
    detail?: string;
}

export const DisputeResolutions: FC<IProps> = (props) => {

    const scorecard = useRecoilValue(EEScorecardAtom);
    const isConsolidatedScorecard = useRecoilValue(ConsolidationAtom);
    const maxConsolidatedDetailLength = 6000 * (scorecard.ConsolidationSourceScorecardsCount ?? 1);
    const maxDetailLength = isConsolidatedScorecard ? maxConsolidatedDetailLength : 6000;
    const [remainingDisputeResolutionCharacters, setRemainingDisputeResolutionCharacters] = useState(maxDetailLength);


    const { control, reset, getValues, setValue, formState: { errors } } = useForm<InputForm>({
        mode: 'all',
        resolver: yupResolver(disputeResolutionsSchema(maxDetailLength))
    });

    const getDisputeResolutionsData = async (scorecardId: string) => {
        try {
            const dataResponse = await DisputeResolutionsService.GetDisputeResolutionsInformation(scorecardId);
            const formData: InputForm = {
                detail: dataResponse.data.detail
            }
            reset(formData);
        } catch (error: any) {
            console.log(error);
        }
    }

    const saveDisputeResolutionsForm = async () => {
        const formData = getValues();

        disputeResolutionsSchema(maxDetailLength).validate(formData, { abortEarly: false }).then(async() => {
            const requestData: DisputeResolutionsDto.DisputeResolutionsDto = {
                scorecardId: props.scorecardId,
                detail: formData.detail
            }
            const response = await DisputeResolutionsService.SaveDisputeResolutionsInformation(requestData);
        }).catch(function (error: any) {
            toast.error(error.message);
        });
    }

    const disputeResolutionsLog = (actionType: number, actionDescription: string) => {
        UserActivityService.createActivityLog({
            section: DisputeResolutionsSection,
            actionType: actionType,
            actionDescription: actionDescription,
            scorecardId: props.scorecardId,
            requestUrl: window.location.href
        });
    }
    
    useEffect(() => {
        getDisputeResolutionsData(props.scorecardId);
        disputeResolutionsLog(UserActivityActionType.View, "View dispute resolution commentary");

        return () => {
            if (localStorage.getItem('coreToken') !== null) {
                saveDisputeResolutionsForm();
                disputeResolutionsLog(UserActivityActionType.Create, "Capture dispute resolution commentary");
            }
        }
    }, [props.scorecardId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box component="form" mt={2}>
            <Box>
                <MDTypography
                    variant='button'
                    fontWeight='regular'
                    style={{ color: !!errors.detail ? 'error' : '#666666', fontSize: "0.85em" }}
                >
                    Detail* (Max {maxDetailLength} characters)
                </MDTypography>
                <Controller
                    control={control}
                    name="detail"
                    render={({ field: { onChange, value } }) => (
                        <Quill
                            initialContent={value || ""}
                            maxLength={maxDetailLength}
                            handleChange={(newValue: string) => {
                                onChange(newValue);
                                setValue('detail', newValue);
                            }}
                            setRemainingCharacters={setRemainingDisputeResolutionCharacters}
                        />
                    )}
                />
                <MDTypography
                    variant="button"
                    fontWeight="regular"
                    style={{  color:
                        remainingDisputeResolutionCharacters < 0
                        ? "#FF0000" 
                        : "#666666", 
                        fontSize: "0.85em"
                    }}
                >
                    {remainingDisputeResolutionCharacters} characters remaining
                </MDTypography>
                {(!!errors.detail) && <FormHelperText error>{errors.detail?.message}</FormHelperText>}
            </Box>
        </Box>
    )
}